﻿<template>
  <div class="ms-input ms-editor w-100" :class="{'ms-validate': isValidate}">
    <label v-if="label" class="label">{{ label }}</label>
    <div class="flex-row align-center" :class="[{error: errorMessage, disabled: disabled}, hasBorder ? 'border' : '']">
      <div v-if="leftIcon" :class="['icon24 icon left', leftIcon]" :title="errorProvider.message"></div>

      <input
        v-if="trimModel"
        ref="input"
        v-model.trim="internalText"
        :title="internalText"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        :type="type"
        :class="[{disabled: disabled}, classInput]"
        v-on="listeners"
      />

      <input
        v-else
        ref="input"
        v-model="internalText"
        :title="internalText"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        :type="type"
        :class="[{disabled: disabled}, classInput]"
        v-on="listeners"
      />

      <div v-if="rightIcon" class="ic-right">
        <div :class="['icon24 icon', rightIcon, isClickRightIcon ? 'pointer' : '']" @click="onClickRightIcon" v-tooltip="tooltipRightIcon"></div>
      </div>
      <slot name="right-input"></slot>
    </div>
    <div v-if="errorMessage" class="error-text">
      {{ errorMessage }}
    </div>
  </div>
</template>
<script>
import {defineComponent, ref, onMounted, computed, watch, nextTick, getCurrentInstance} from 'vue';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import {useValidateControl} from '@/composables/validateControl.js';
export default defineComponent({
  name: 'MsInput',
  extends: msBaseComponent,
  props: {
    /**
     * V-model bắn ra ngoài binding 2 chiều
     */
    modelValue: {
      default: null,
    },
    /**
     * Hiện thị chũ mờ ở dưới ô input
     * Created by LTDAT(22.06.2020)
     */
    placeholder: {
      default: null,
      type: [Number, String],
    },
    // icon left
    leftIcon: {
      default: null,
      type: String,
    },
    // icon right
    rightIcon: {
      default: null,
      type: String,
    },
    // Giới hạn nhập của ô input
    maxLength: {
      default: null,
      type: Number,
    },
    // disabled
    disabled: {
      default: false,
      type: Boolean,
    },
    // Xác định xem input có border hay không?
    hasBorder: {
      default: true,
      type: Boolean,
    },
    /*
     * Hàm show ra câu thông báo riêng theo từng trường hợp
     * pvduy 18/03/2021
     * ví dụ: customWarring {rules:'required', mes: 'Tài sản này là của pvduy'}
     */
    customWarring: {
      type: Object,
      default: null,
    },
    /**
     * Có được click icon hay không?
     * NNLINH 27.06.2022
     */
    isClickRightIcon: {
      default: false,
      type: Boolean,
    },
    trimModel: {
      default: false,
      type: Boolean,
    },
    type: {
      default: 'text',
      type: String,
    },
    percent: {
      default: false,
      type: Boolean,
    },
    classInput: {
      default: '',
      type: String,
    },
    tooltipRightIcon: {
      default: '',
      type: String,
    },
    /**
     * Chặn event paste
     */
    preventPaste: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['msKeydown', 'update:modelValue', 'focus', 'blur', 'change', 'msKeyup', 'clickRightIcon'],
  setup(props, {emit}) {
    // Variable
    const internalText = ref('');
    let focused = false;
    let displayValue = null;
    let scrollWidth = 0;
    const input = ref(null);
    const {errorMessage, validate, isValidate, clearValidate} = useValidateControl({
      props,
    });

    const {proxy} = getCurrentInstance();

    // Method
    const hasTooltip = () => {
      if (scrollWidth > input?.value?.offsetWidth) {
        return true;
      }
      return false;
    };

    /**
     * Xử lý event keydown
     */
    function keyDownInput(e) {
      let check = true;

      if (!e.ctrlKey && e.key?.length == 1 && proxy.regexMask) {
        check = proxy.checkRegex(e, e.key);
      }

      if (check) {
        emit(proxy.$ms.constant.CustomEvent.MsKeydown, e, internalText.value);
      } else {
        e.preventDefault();
      }
    }
    // Sự kiện focus vào ô input
    const onFocus = e => {
      focused = true;
      emit('focus', internalText.value, e);
    };

    // Sự kiên blur ra ngoài ô input
    const onBlur = e => {
      focused = false;
      validate();
      emit('blur', internalText.value, e);
    };

    /**
     * Sự kiện Click icon bên phải ô Input
     * NNLINH 27.06.2022
     */
    const onClickRightIcon = () => {
      if (props.isClickRightIcon) {
        emit('clickRightIcon');
      }
    };

    // Reset lại giá trị về ban đầu
    const reset = () => {
      internalText.value = '';
      displayValue = null;
    };
    // Set giá trị cho ô input
    const setValue = (value, oldValue) => {
      if (proxy.rules) {
        const rule = proxy.rules.find(x => x.name === 'pattern' && x.compareValue === 'phone');
        if (rule && value) {
          value = value.replace(/ /g, '').replace(/\./g, '');
        }
      }
      if (props.percent) {
        if (value && value < 0) value = -value;
        else if (value > 100) value = oldValue;
      }
      internalText.value = value;
    };

    // Lấy giá trị ô input
    const getValue = () => {
      return internalText.value;
    };

    /**
     * Hủy lan rộng event
     * HTHIEP 09.02.2022
     */
    const cancelEvent = e => {
      if (e) {
        if (typeof e.preventDefault === 'function') {
          e.preventDefault();
        }

        if (typeof e.stopPropagation === 'function') {
          e.stopPropagation();
        }

        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
      }
    };

    // Hooks
    // set internalText
    onMounted(() => {
      internalText.value = props.modelValue;
    });
    
    /**
     * Xử lý paste value
     */
    function pasteInput(e) {
      if (proxy.preventPaste) {
        e.preventDefault();
      }
    }

    /**
     * Lắng nghe event => thò ra ngoài
     * HTHIEP 11.02.2022: Làm mịn, migration sang vue 3
     */
    const listeners = computed(() => {
      return {
        blur: e => {
          cancelEvent(e);
          onBlur(e);
        },
        focus: e => {
          cancelEvent(e);
          onFocus(e);
        },
        change: e => {
          cancelEvent(e);
          emit('change', e, internalText.value);
        },
        keydown: e => {
          proxy.keyDownInput(e);
        },
        keyup: e => {
          emit('msKeyup', e, internalText.value);
        },
        paste: e => {
          pasteInput(e);
        },
      };
    });

    /**
     * Lắng nghe xử lý update lại modelValue
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => internalText.value,
      value => {
        if (value !== props.modelValue) {
          emit('update:modelValue', value);
          scrollWidth = input.value.scrollWidth;
        }
      },
    );

    /**
     * Lắng nghe thay đổi modelValue set lại text cho input
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => props.modelValue,
      (value, oldValue) => {
        setValue(value, oldValue);
      },
    );
    return {
      internalText,
      focused,
      listeners,
      reset,
      hasTooltip,
      displayValue,
      input,
      getValue,
      errorMessage,
      validate,
      keyDownInput,
      isValidate,
      clearValidate,
      onClickRightIcon,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsInput.scss';
.ic-right {
  display: flex;
  background: var(--form-white);
  align-items: center;
  width: $input-height;
  justify-content: center;
}

.align-center {
  align-items: center;
}

.error {
  .icon24 {
    &.search-input::before {
      background: transparent var(--image-icon) no-repeat !important;
      background-position: -367px -196px !important;
    }
  }
}
</style>
